// Inbox — department contact messages (Sales · Jobs · PR) with reply, status and addresses.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Archive, Inbox, Loader2, Mail, Paperclip, RefreshCw, Reply, Save, Settings, Undo2 } from 'lucide-react';
import { API_URL, apiCall } from './_shared';
import UnfinishedBookings from './UnfinishedBookings';
import { UpsellRequests } from './GrowthCards';
import { UpsellMenuEditor } from './UpsellMenuEditor';

const DEPS = [
  { key: 'sales', label: 'Sales', color: 'bg-emerald-100 text-emerald-700' },
  { key: 'jobs', label: 'Jobs', color: 'bg-sky-100 text-sky-700' },
  { key: 'pr', label: 'PR', color: 'bg-violet-100 text-violet-700' },
  { key: 'pricematch', label: 'Price match', color: 'bg-amber-100 text-amber-800' },
];
const OUTCOMES = [
  { key: 'pending', label: 'Pending', color: 'bg-orange-100 text-orange-700' },
  { key: 'matched', label: 'Matched — price adjusted', color: 'bg-green-100 text-green-700' },
  { key: 'declined', label: 'Declined — conditions not met', color: 'bg-slate-200 text-slate-700' },
  { key: 'no_contact', label: 'No WhatsApp message arrived', color: 'bg-slate-100 text-slate-500' },
];

const ClaimOutcome = ({ item, onUpdated }) => {
  const c = item.claim || {};
  const [f, setF] = useState({ outcome: c.outcome || 'pending', competitor: c.competitor || '', their_price: c.their_price || '', note: item.note || '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    setBusy(true); setErr('');
    try { onUpdated(await apiCall(`/api/admin/contact/messages/${item.id}/claim`, { method: 'PUT', body: JSON.stringify(f) })); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const cur = OUTCOMES.find((o) => o.key === c.outcome) || OUTCOMES[0];
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3" data-testid="claim-outcome">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-bold text-slate-900">Claim</span>
        <span className="text-slate-700">{c.room} · {c.dates} · from the {c.source} page{c.taps > 1 ? ` · tapped ${c.taps}×` : ''}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${cur.color}`} data-testid="claim-outcome-badge">{cur.label}</span>
        {c.decided_by && <span className="text-xs text-slate-500">by {c.decided_by} · {String(c.decided_at).slice(0, 16).replace('T', ' ')}</span>}
      </div>
      <div className="grid sm:grid-cols-3 gap-2 text-sm">
        <select value={f.outcome} onChange={(e) => setF({ ...f, outcome: e.target.value })} className="border rounded-lg px-3 py-2 bg-white" data-testid="claim-outcome-select">
          {OUTCOMES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <input value={f.competitor} onChange={(e) => setF({ ...f, competitor: e.target.value })} placeholder="Competitor site (e.g. Booking.com)" className="border rounded-lg px-3 py-2" data-testid="claim-competitor" />
        <input value={f.their_price} onChange={(e) => setF({ ...f, their_price: e.target.value })} placeholder="Their price (e.g. €62/night)" className="border rounded-lg px-3 py-2" data-testid="claim-price" />
      </div>
      <textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} rows={2} placeholder="Note for the team (what was agreed, booking number…)" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="claim-note" />
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={busy} className="flex items-center gap-1 px-4 py-2 rounded bg-slate-900 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-50" data-testid="claim-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save outcome</button>
        {err && <span className="text-xs text-red-600" data-testid="claim-error">{err}</span>}
      </div>
    </div>
  );
};
const STATUS_COLOR = { new: 'bg-orange-100 text-orange-700', replied: 'bg-green-100 text-green-700', archived: 'bg-slate-100 text-slate-500' };

const REQ_OUTCOMES = [
  { key: 'pending', label: 'Pending — check availability', color: 'bg-orange-100 text-orange-700' },
  { key: 'confirmed', label: 'Confirmed — booked in Beds24', color: 'bg-green-100 text-green-700' },
  { key: 'declined', label: 'Declined — no availability', color: 'bg-slate-200 text-slate-700' },
];

const PartnerRequest = ({ item, onUpdated }) => {
  const p = item.partner || {};
  const [f, setF] = useState({ outcome: p.outcome || 'pending', booking_ref: p.booking_ref || '', note: p.outcome_note || '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    setBusy(true); setErr('');
    try { onUpdated(await apiCall(`/api/admin/tour-operators/requests/${item.id}`, { method: 'PUT', body: JSON.stringify(f) })); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const cur = REQ_OUTCOMES.find((o) => o.key === p.outcome) || REQ_OUTCOMES[0];
  const opts = REQ_OUTCOMES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>);
  const fmtD = (d) => (d ? d.split('-').reverse().join('.') : '');
  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 space-y-3" data-testid="partner-request">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-bold text-slate-900">Tour operator request</span>
        <span className="text-slate-700">{p.operator_name} · {p.commission_pct}% commission</span>
        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${cur.color}`} data-testid="partner-request-badge">{cur.label}</span>
        {p.outcome_by && <span className="text-xs text-slate-500">by {p.outcome_by} · {String(p.outcome_at).slice(0, 16).replace('T', ' ')}</span>}
      </div>
      <div className="grid sm:grid-cols-4 gap-2 text-sm">
        <div className="bg-white rounded-lg border px-3 py-2"><div className="text-[11px] text-slate-500">Room</div><div className="font-semibold text-slate-900">{p.room_name}</div></div>
        <div className="bg-white rounded-lg border px-3 py-2"><div className="text-[11px] text-slate-500">Dates</div><div className="font-semibold text-slate-900">{fmtD(p.check_in)} – {fmtD(p.check_out)} · {p.nights} n</div></div>
        <div className="bg-white rounded-lg border px-3 py-2"><div className="text-[11px] text-slate-500">Guests</div><div className="font-semibold text-slate-900">{p.adults} ad{p.children ? ` + ${p.children} ch` : ''}{p.guest_name ? ` · ${p.guest_name}` : ''}</div></div>
        <div className="bg-white rounded-lg border px-3 py-2" data-testid="partner-request-netto"><div className="text-[11px] text-slate-500">Netto total (brutto €{Number(p.brutto_total || 0).toFixed(2)})</div><div className="font-bold text-[#E77500]">€{Number(p.netto_total || 0).toFixed(2)}</div></div>
      </div>
      <div className="grid sm:grid-cols-3 gap-2 text-sm">
        <select value={f.outcome} onChange={(e) => setF({ ...f, outcome: e.target.value })} className="border rounded-lg px-3 py-2 bg-white" data-testid="partner-request-outcome">{opts}</select>
        <input value={f.booking_ref} onChange={(e) => setF({ ...f, booking_ref: e.target.value })} placeholder="Beds24 booking number" className="border rounded-lg px-3 py-2" data-testid="partner-request-ref" />
        <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note (deposit, alternative room…)" className="border rounded-lg px-3 py-2" data-testid="partner-request-note" />
      </div>
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={busy} className="flex items-center gap-1 px-4 py-2 rounded bg-slate-900 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-50" data-testid="partner-request-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save outcome</button>
        {err && <span className="text-xs text-red-600">{err}</span>}
      </div>
    </div>
  );
};

const DepartmentSettings = ({ onClose }) => {
  const [deps, setDeps] = useState(null);
  const [msg, setMsg] = useState('');
  useEffect(() => { apiCall('/api/admin/contact/departments').then((d) => setDeps(d.departments)).catch((e) => setMsg(e.message)); }, []);
  const save = async () => {
    try {
      const d = await apiCall('/api/admin/contact/departments', { method: 'PUT', body: JSON.stringify({ departments: deps }) });
      setDeps(d.departments); setMsg('Saved.');
    } catch (e) { setMsg(e.message); }
  };
  if (!deps) return <div className="p-4 text-sm text-slate-500">Loading…</div>;
  const rows = Object.entries(deps).map(([k, v]) => (
    <div key={k} className="grid sm:grid-cols-[120px_1fr_1fr] gap-2 items-center" data-testid={`inbox-dep-${k}`}>
      <span className="text-sm font-semibold text-slate-800">{v.label}</span>
      <input value={v.email || ''} onChange={(e) => setDeps({ ...deps, [k]: { ...v, email: e.target.value } })} placeholder="department@larosa-negra.com" className="px-3 py-2 border rounded text-sm" data-testid={`inbox-dep-email-${k}`} />
      <input value={v.hint || ''} onChange={(e) => setDeps({ ...deps, [k]: { ...v, hint: e.target.value } })} placeholder="Hint shown on the website" className="px-3 py-2 border rounded text-sm" />
    </div>
  ));
  return (
    <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="inbox-settings">
      <p className="text-sm text-slate-600">Messages are stored here and emailed to these addresses (sent from the hotel Gmail account once e-mail delivery is configured).</p>
      {rows}
      <div className="flex items-center gap-3">
        <button onClick={save} className="flex items-center gap-1 px-3 py-1.5 rounded bg-slate-900 text-white text-sm font-semibold" data-testid="inbox-settings-save"><Save className="w-4 h-4" /> Save</button>
        <button onClick={onClose} className="text-sm text-slate-500 hover:underline">Close</button>
        {msg && <span className="text-xs text-slate-600">{msg}</span>}
      </div>
    </div>
  );
};

const MessageDetail = ({ item, onUpdated, mail }) => {
  const [reply, setReply] = useState('');
  const [subject, setSubject] = useState('');
  const [quote, setQuote] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { setSubject(`Re: ${item.subject || 'your message'} — Hotel La Rosa Negra`); setReply(''); }, [item.id, item.subject]);
  const act = async (fn) => { setBusy(true); setErr(''); try { onUpdated(await fn()); } catch (e) { setErr(e.message); } finally { setBusy(false); } };
  const setStatus = (status) => act(() => apiCall(`/api/admin/contact/messages/${item.id}`, { method: 'PUT', body: JSON.stringify({ status }) }));
  const sendReply = () => act(async () => {
    const r = await apiCall(`/api/admin/contact/messages/${item.id}/reply`, { method: 'POST', body: JSON.stringify({ text: reply, subject, include_original: quote }) });
    setReply('');
    return r;
  });
  const preview = async () => {
    try {
      const r = await apiCall(`/api/admin/contact/messages/${item.id}/reply-preview?text=${encodeURIComponent(reply)}&include_original=${quote}`);
      const w = window.open('', '_blank');
      if (w) { w.document.write(`<!doctype html><title>Reply preview</title><body style="padding:24px;background:#f8fafc"><div style="max-width:640px;margin:auto;background:#fff;padding:24px;border-radius:12px">${r.html}</div></body>`); w.document.close(); }
    } catch (e) { setErr(e.message); }
  };
  const dep = DEPS.find((d) => d.key === item.department);
  const replies = (item.replies || []).map((r) => (
    <div key={r.id} className="bg-green-50 border border-green-100 rounded-lg p-3 text-sm" data-testid={`inbox-reply-${r.id}`}>
      <div className="text-xs text-green-700 mb-1">Reply by {r.by} · {r.at.slice(0, 16).replace('T', ' ')} · {r.email_sent ? `e-mailed from ${r.sent_from || 'hotel mailbox'}` : `not e-mailed (${r.email_error || 'e-mail not configured'})`}</div>
      {r.subject && <div className="text-xs text-slate-500 mb-1">{r.subject}</div>}
      <div className="whitespace-pre-wrap text-slate-800">{r.text}</div>
    </div>
  ));
  return (
    <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="inbox-detail">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${dep?.color}`}>{dep?.label}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_COLOR[item.status]}`} data-testid="inbox-detail-status">{item.status}</span>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mt-1">{item.subject || '(no subject)'}</h3>
          <p className="text-sm text-slate-600">{item.name}{item.email && <> · <a href={`mailto:${item.email}`} className="text-orange-600 hover:underline">{item.email}</a></>}{item.phone && ` · ${item.phone}`} · {item.created_at.slice(0, 16).replace('T', ' ')} · {item.lang}</p>
        </div>
        <div className="flex gap-1">
          {item.status !== 'archived'
            ? <button onClick={() => setStatus('archived')} disabled={busy} className="flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-slate-50" data-testid="inbox-archive"><Archive className="w-3.5 h-3.5" /> Archive</button>
            : <button onClick={() => setStatus('new')} disabled={busy} className="flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-slate-50" data-testid="inbox-unarchive"><Undo2 className="w-3.5 h-3.5" /> Reopen</button>}
        </div>
      </div>
      <div className="whitespace-pre-wrap text-sm text-slate-800 bg-slate-50 rounded-lg p-3" data-testid="inbox-detail-message">{item.message}</div>
      {item.attachment_url && (
        <a href={`${API_URL}${item.attachment_url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-orange-600 hover:underline" data-testid="inbox-attachment">
          <Paperclip className="w-4 h-4" /> {item.attachment_name || 'Attachment'}
        </a>
      )}
      {item.kind === 'price_match' && <ClaimOutcome item={item} onUpdated={onUpdated} />}
      {item.kind === 'partner_request' && <PartnerRequest item={item} onUpdated={onUpdated} />}
      {!item.email_sent && item.kind !== 'price_match' && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">Not forwarded to the department address ({item.email_error || 'e-mail not configured'}) — reply from here.</p>}
      {replies.length > 0 && <div className="space-y-2">{replies}</div>}
      {item.email && <div className="space-y-2" data-testid="inbox-reply-box">
        <div className="text-xs text-slate-500 flex items-center gap-1" data-testid="inbox-reply-from">
          <Mail className="w-3.5 h-3.5" />
          {mail?.configured ? <>Reply goes to <b>{item.email}</b> from <b>{mail.sender}</b>{item.ack_email_id ? ' · threaded under the auto-reply the guest already received' : ''}</> : <span className="text-amber-700">E-mail delivery is not configured — the reply will be saved but not sent.</span>}
        </div>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full border rounded-lg px-3 py-1.5 text-sm" placeholder="Subject" data-testid="inbox-reply-subject" />
        <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={5} placeholder={`Dear ${item.name},\n\n…`} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="inbox-reply-text" />
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={sendReply} disabled={busy || reply.trim().length < 2} className="flex items-center gap-1 px-4 py-2 rounded bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-50" data-testid="inbox-reply-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Reply className="w-4 h-4" />} Send reply
          </button>
          <button onClick={preview} disabled={reply.trim().length < 2} className="text-sm text-slate-600 hover:underline disabled:opacity-50" data-testid="inbox-reply-preview">Preview e-mail</button>
          <label className="inline-flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={quote} onChange={(e) => setQuote(e.target.checked)} data-testid="inbox-reply-quote" /> quote original message</label>
          {err && <span className="text-xs text-red-600" data-testid="inbox-reply-error">{err}</span>}
        </div>
      </div>}
    </div>
  );
};

const ContactInbox = ({ userRole }) => {
  const [dep, setDep] = useState('');
  const [status, setStatus] = useState('new');
  const [q, setQ] = useState('');
  const [data, setData] = useState({ items: [], counts: {} });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const canSettings = ['super_admin', 'admin', 'manager', 'marketing'].includes(userRole);

  const reqRef = useRef(0);
  const load = useCallback(async () => {
    const my = ++reqRef.current;
    setLoading(true);
    try {
      const d = await apiCall(`/api/admin/contact/messages?department=${dep}&status=${status}&q=${encodeURIComponent(q)}`);
      if (my !== reqRef.current) return;
      setData(d);
      setSelected((s) => (s ? d.items.find((i) => i.id === s.id) || s : d.items[0] || null));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [dep, status, q]);
  useEffect(() => { load(); }, [load]);

  const onUpdated = (item) => {
    setSelected(item);
    setData((d) => ({ ...d, items: d.items.map((i) => (i.id === item.id ? item : i)) }));
    load();
    window.dispatchEvent(new CustomEvent('inbox-changed'));
  };

  const depChips = [{ key: '', label: 'All' }, ...DEPS].map((d) => (
    <button key={d.key} onClick={() => setDep(d.key)} className={`px-3 py-1 rounded-full text-xs font-semibold ${dep === d.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-testid={`inbox-dep-filter-${d.key || 'all'}`}>
      {d.label}{d.key && data.counts[d.key] ? ` (${data.counts[d.key]})` : ''}
    </button>
  ));
  const statusChips = ['new', 'replied', 'archived', 'all'].map((s) => (
    <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1 rounded-full text-xs font-semibold ${status === s ? 'bg-orange-500 text-white' : 'bg-white border text-slate-700'}`} data-testid={`inbox-status-${s}`}>{s}</button>
  ));
  const list = data.items.map((i) => {
    const d = DEPS.find((x) => x.key === i.department);
    return (
      <button key={i.id} onClick={() => setSelected(i)} className={`w-full text-left px-3 py-2.5 border-b hover:bg-orange-50 ${selected?.id === i.id ? 'bg-orange-50' : ''}`} data-testid={`inbox-item-${i.id}`}>
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-sm text-slate-900 truncate">{i.name}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${d?.color}`}>{d?.label}</span>
        </div>
        <div className="text-xs text-slate-600 truncate">{i.subject || i.message}</div>
        <div className="text-[11px] text-slate-400 flex items-center gap-2">{i.created_at.slice(0, 10)}{i.attachment_url && <Paperclip className="w-3 h-3" />}{i.status !== 'new' && <span>{i.status}</span>}</div>
      </button>
    );
  });

  return (
    <div className="space-y-4" data-testid="contact-inbox">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Inbox className="w-6 h-6 text-orange-500" /> Inbox <span className="text-sm font-semibold text-orange-600" data-testid="inbox-new-count">{data.counts.new || 0} new</span></h2>
          <p className="text-sm text-slate-500">Website messages for Sales, Jobs applications and Public relations, every price-match claim tapped on Beds24 or the website, and booking requests from tour-operator partner portals (Sales).{data.mail?.configured && <> Replies are sent from <b>{data.mail.sender}</b>.</>}</p>
        </div>
        <div className="flex items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, text…" className="px-3 py-1.5 border rounded text-sm w-56" data-testid="inbox-search" />
          <button onClick={load} className="p-2 border rounded hover:bg-slate-50" title="Refresh"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
          {canSettings && <button onClick={() => setShowSettings((v) => !v)} className="flex items-center gap-1 px-3 py-1.5 border rounded text-sm hover:bg-slate-50" data-testid="inbox-settings-toggle"><Settings className="w-4 h-4" /> Addresses</button>}
        </div>
      </div>
      {showSettings && <DepartmentSettings onClose={() => setShowSettings(false)} />}
      <div className="flex flex-wrap gap-2 items-center">{depChips}<span className="w-px h-5 bg-slate-200 mx-1" />{statusChips}</div>
      <div className="grid lg:grid-cols-[360px_1fr] gap-4">
        <div className="bg-white border rounded-xl overflow-hidden max-h-[70vh] overflow-y-auto" data-testid="inbox-list">
          {list.length ? list : <div className="p-6 text-sm text-slate-400 text-center flex flex-col items-center gap-2"><Mail className="w-6 h-6" /> No messages here.</div>}
        </div>
        {selected ? <MessageDetail item={selected} onUpdated={onUpdated} mail={data.mail} /> : <div className="bg-white border rounded-xl p-6 text-sm text-slate-400">Select a message to read and reply.</div>}
      </div>
      <UnfinishedBookings />
      <UpsellRequests />
      {['super_admin', 'admin', 'manager', 'marketing'].includes(userRole) && <UpsellMenuEditor />}
    </div>
  );
};

export default ContactInbox;
